import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';

import { isAnonymousPhone } from '../../../helper/phone-utils/is-anonymous-phone/is-anonymous-phone';
import { DBDocObject } from '../../generic/db-doc/db-doc-object';
import { DBDocSchema } from '../../generic/db-doc/db-doc-schema';

import { BlockedCallerConstructor } from './blocked-caller-constructor';
import { BlockedCallerSchema } from './blocked-caller-schema';

export class BlockedCaller extends DBDocObject {
  /////////////////////////////////////////////////////////////////////////////
  // Variables
  /////////////////////////////////////////////////////////////////////////////

  protected sessionId!: string;

  protected from!: string;

  protected anonymousCaller!: boolean;

  protected expireTime!: moment.Moment;

  protected reason!: string;

  /////////////////////////////////////////////////////////////////////////////
  // Constructor
  /////////////////////////////////////////////////////////////////////////////

  constructor(parameters: BlockedCallerConstructor) {
    super(parameters);
  }

  /////////////////////////////////////////////////////////////////////////////
  // Deserialize
  /////////////////////////////////////////////////////////////////////////////

  /**
   * This static function is private, and meant to be called only by
   * SerializableObject, and subclasses
   *
   * @param validationResult
   */
  protected static _deserialize(validationResult: import('joi').ValidationResult): BlockedCaller {
    return new BlockedCaller(super._deserialize(validationResult));
  }

  /////////////////////////////////////////////////////////////////////////////
  // Serialize
  /////////////////////////////////////////////////////////////////////////////

  public serialize(): any {
    return super.serialize(BlockedCaller.getSchema());
  }

  /////////////////////////////////////////////////////////////////////////////
  // Schema
  /////////////////////////////////////////////////////////////////////////////

  public static getSchema(): DBDocSchema {
    return new BlockedCallerSchema();
  }

  /////////////////////////////////////////////////////////////////////////////
  // Getters
  /////////////////////////////////////////////////////////////////////////////

  public getSessionId(): string {
    return cloneDeep(this.sessionId);
  }

  /**
   * Unique identifier of the caller.
   *
   * The phone number, SIP address, Client identifier or SIM SID that made this call.
   * Phone numbers are in E.164 format (e.g., +16175551212). SIP addresses are
   * formatted as name@company.com. Client identifiers are formatted client:name.
   * SIM SIDs are formatted as sim:sid.
   *
   * See https://www.twilio.com/docs/voice/api/call-resource#call-properties
   */
  public getFrom(): string {
    return cloneDeep(this.from);
  }

  public getAnonymousCaller(): boolean {
    return cloneDeep(this.anonymousCaller);
  }

  public getExpireTime(): moment.Moment {
    return cloneDeep(this.expireTime);
  }

  public getReason(): string {
    return cloneDeep(this.reason);
  }

  /////////////////////////////////////////////////////////////////////////////
  // Setters
  /////////////////////////////////////////////////////////////////////////////

  public setExpireTime(expireTime: moment.Moment) {
    this.expireTime = expireTime;
    return this;
  }

  public setReason(reason: string) {
    this.reason = reason;
    return this;
  }

  /////////////////////////////////////////////////////////////////////////////
  // Other Methods
  /////////////////////////////////////////////////////////////////////////////

  /**
   * Return true if this object specifies that the given twilio formatted from
   * field should be blocked, and false otherwise.
   */
  public doesBlock(fromField: string): boolean {
    if (this.getFrom().trim() === fromField.trim()) {
      return true;
    }

    if (this.getAnonymousCaller() && isAnonymousPhone(fromField)) {
      return true;
    }

    return false;
  }
}
